"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Send, RotateCcw, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ragApi } from "@/services/ApiService"

/* ─── Types (same as main for merge compat) ─────────────── */

interface Message {
  id: string
  content: string
  role: "user" | "assistant"
  timestamp: Date
}

type ConversationHistoryItem = {
  role: "user" | "assistant"
  content: string
}

interface RagTesterProps {
  translations?: Record<string, unknown>
  categories?: unknown[]
  messages?: Message[]
  onMessagesChange?: React.Dispatch<React.SetStateAction<Message[]>>
}

const TESTER_AGENT_ID = "tester-default"

type InvokeResponse = {
  response_text?: string
  messages?: Array<{ content?: string; text?: string } | string>
  error?: string
}

type InvokeEnvelope = InvokeResponse | { success?: boolean; data?: InvokeResponse }

function unwrapInvokeResponse(response: InvokeEnvelope): InvokeResponse {
  if (response && typeof response === "object" && "success" in response && "data" in response) {
    return response.data ?? {}
  }
  return response as InvokeResponse
}

function extractAssistantResponseText(rawResponse: InvokeEnvelope): string {
  const response = unwrapInvokeResponse(rawResponse)
  if (response.response_text?.trim()) {
    return response.response_text
  }

  const messages = response.messages
  if (Array.isArray(messages) && messages.length > 0) {
    const lastMessage = messages[messages.length - 1]
    if (typeof lastMessage === "string" && lastMessage.trim()) {
      return lastMessage
    }
    if (typeof lastMessage === "object" && lastMessage) {
      if (typeof lastMessage.content === "string" && lastMessage.content.trim()) {
        return lastMessage.content
      }
      if (typeof lastMessage.text === "string" && lastMessage.text.trim()) {
        return lastMessage.text
      }
    }
  }

  if (response.error?.trim()) {
    return response.error
  }

  return "No response received from agent."
}

/* ─── Input Box Component ───────────────────────────────── */

function ChatInput({
  inputRef,
  inputValue,
  setInputValue,
  isLoading,
  onSend,
  placeholder,
  large,
}: {
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  inputValue: string
  setInputValue: (v: string) => void
  isLoading: boolean
  onSend: () => void
  placeholder: string
  large?: boolean
}) {
  // auto-resize
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`
    }
  }, [inputValue, inputRef])

  return (
    <div className={`w-full rounded-lg border bg-muted/40 shadow-sm transition-colors focus-within:border-primary/40 focus-within:bg-background focus-within:shadow-md ${large ? "max-w-2xl" : ""}`}>
      <Textarea
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            onSend()
          }
        }}
        disabled={isLoading}
        placeholder={placeholder}
        rows={1}
        className={`min-h-0 w-full resize-none border-0 bg-transparent px-4 leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0 ${large ? "pt-4 pb-2" : "pt-3 pb-2"}`}
        style={{ maxHeight: 150 }}
      />
      <div className="flex items-center justify-end px-3 pb-2.5">
        <Button
          type="submit"
          size="icon-xs"
          disabled={isLoading || !inputValue.trim()}
          onClick={(e) => { e.preventDefault(); onSend() }}
          className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-20 transition-opacity"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

/* ─── Component ─────────────────────────────────────────── */

export function RagTester({ translations, messages: controlledMessages, onMessagesChange }: RagTesterProps) {
  /* ── i18n helper ──────────────────────────────────────── */
  const t = (key: string): string => {
    if (!translations) return key
    const result = key.split(".").reduce((obj: unknown, k: string) => {
      if (obj && typeof obj === "object" && k in obj)
        return (obj as Record<string, unknown>)[k]
      return undefined
    }, translations)
    return typeof result === "string" ? result : key
  }

  /* ── state ────────────────────────────────────────────── */
  const [localMessages, setLocalMessages] = React.useState<Message[]>([])
  const [inputValue, setInputValue] = React.useState("")
  const [isLoading, setIsLoading] = React.useState(false)
  const [fadeOut, setFadeOut] = React.useState(false)
  const messagesEndRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const messages = controlledMessages ?? localMessages

  const setMessages = React.useCallback((updater: React.SetStateAction<Message[]>) => {
    if (onMessagesChange) {
      onMessagesChange(updater)
      return
    }
    setLocalMessages(updater)
  }, [onMessagesChange])

  const buildConversationHistory = React.useCallback((items: Message[]): ConversationHistoryItem[] => {
    return items
      .filter((item) => item.content.trim().length > 0)
      .map((item) => ({
        role: item.role,
        content: item.content,
      }))
  }, [])

  /* ── auto-scroll ──────────────────────────────────────── */
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isLoading])

  /* ── send ─────────────────────────────────────────────── */
  const handleSend = async () => {
    const trimmed = inputValue.trim()
    if (!trimmed || isLoading) return

    // Fade-out empty state on first message (like MUVERAN Brain)
    const isFirstMessage = messages.length === 0
    if (isFirstMessage) { setFadeOut(true); await new Promise(r => setTimeout(r, 350)) }

    const userMsg: Message = {
      id: Date.now().toString(),
      content: trimmed,
      role: "user",
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setInputValue("")
    setIsLoading(true)
    setFadeOut(false)

    try {
      const conversationHistory = buildConversationHistory(messages)
      const response = await ragApi.post(
        `/api/agent/v1/agents/${TESTER_AGENT_ID}/invoke/`,
        {
          message: trimmed,
          conversation_history: conversationHistory,
          source: "web",
        }
      ) as InvokeEnvelope

      const reply: Message = {
        id: (Date.now() + 1).toString(),
        content: extractAssistantResponseText(response),
        role: "assistant",
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, reply])
    } catch {
      const translatedError = t("knowledge.rag.error_generic")
      const fallbackError = translatedError === "knowledge.rag.error_generic"
        ? "Sorry, I could not process that right now. Please try again."
        : translatedError

      const errorReply: Message = {
        id: (Date.now() + 1).toString(),
        content: fallbackError,
        role: "assistant",
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorReply])
    } finally {
      setIsLoading(false)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  /* ── suggestion click ─────────────────────────────────── */
  const handleSuggestion = (text: string) => {
    setInputValue(text)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 50)
  }

  /* ── clear chat ───────────────────────────────────────── */
  const clearChat = () => {
    setMessages([])
    setInputValue("")
    setFadeOut(false)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const hasMessages = messages.length > 0

  /* ═══════════════════════════════════════════════════════ */
  /* ── EMPTY STATE (ChatGPT-style centered) ──────────── */
  /* ═══════════════════════════════════════════════════════ */
  if (!hasMessages && !isLoading) {
    return (
      <div className={cn(
        "flex h-full flex-col items-center justify-center px-6 transition-all duration-300",
        fadeOut && "opacity-0 scale-95"
      )}>
        {/* title */}
        <div className="mb-8 flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center text-primary">
            <Sparkles className="h-6 w-6 text-primary/70" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {t("knowledge.rag.empty_state_title")}
          </h2>
        </div>

        {/* centered input */}
        <div className="w-full max-w-2xl">
          <ChatInput
            inputRef={inputRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            isLoading={isLoading}
            onSend={handleSend}
            placeholder={t("knowledge.rag.input_placeholder")}
            large
          />
        </div>

        {/* suggestion chips */}
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {[
            t("knowledge.rag.suggestion_1"),
            t("knowledge.rag.suggestion_2"),
            t("knowledge.rag.suggestion_3"),
          ].map((s) => (
            <Button
              key={s}
              variant="outline"
              size="xs"
              onClick={() => handleSuggestion(s)}
              className="text-muted-foreground hover:text-foreground"
            >
              {s}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  /* ═══════════════════════════════════════════════════════ */
  /* ── CONVERSATION MODE ─────────────────────────────── */
  /* ═══════════════════════════════════════════════════════ */
  return (
    <div className="flex h-full flex-col">
      {/* ─── Clear chat button (top-right, subtle) ──────── */}
      <div className="flex items-center justify-end px-6 py-2">
        <Button
          variant="ghost"
          size="xs"
          onClick={clearChat}
          className="gap-2 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-3 w-3" />
          {t("knowledge.rag.clear_chat")}
        </Button>
      </div>

      {/* ─── Messages ───────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="px-6 pb-4">
            <div className="mx-auto max-w-3xl">
              {messages.map((msg) => (
                <div key={msg.id} className="mb-5">
                  {msg.role === "user" ? (
                    /* ── User message: right-aligned bubble ── */
                    <div className="flex justify-end">
                      <div className="max-w-[85%] rounded-lg rounded-tr-md bg-primary px-4 py-2.5 text-sm leading-relaxed text-primary-foreground">
                        <span className="whitespace-pre-wrap break-words">{msg.content}</span>
                      </div>
                    </div>
                  ) : (
                    /* ── Assistant message: left, no avatar ── */
                    <div className="text-sm leading-relaxed text-foreground">
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:mb-2 prose-p:last:mb-0 prose-headings:mb-2 prose-headings:mt-4 prose-headings:first:mt-0 prose-ul:mb-2 prose-ol:mb-2 prose-li:ml-1 prose-blockquote:border-l-primary/40 prose-blockquote:bg-primary/5 prose-blockquote:py-0.5 prose-blockquote:not-italic prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:before:content-none prose-code:after:content-none prose-strong:text-foreground">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* loading — matches MUVERAN Brain animation */}
              {isLoading && (
                <div className="mb-5 flex items-center gap-2 animate-in fade-in duration-300">
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 [animation-delay:0ms]" />
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 [animation-delay:150ms]" />
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 [animation-delay:300ms]" />
                  </div>
                  <span className="text-xs text-muted-foreground animate-pulse">
                    {t("knowledge.rag.thinking")}
                  </span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* ─── Input (pinned bottom, centered) ─────────────── */}
      <div className="bg-background px-6 pb-5 pt-2">
        <div className="mx-auto max-w-3xl">
          <ChatInput
            inputRef={inputRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            isLoading={isLoading}
            onSend={handleSend}
            placeholder={t("knowledge.rag.input_placeholder")}
          />
        </div>
      </div>
    </div>
  )
}
